import { createEffect, createSignal, onCleanup, onMount, type Accessor } from "solid-js";
import { nextTabCollapseState } from "./tab-collapse";

// Measured (not media-queried) responsive collapse for the header tab
// bar: when the full-label nav would overflow its cell, the tabs drop to
// icons and the brand shrinks to its seal in lockstep. Hysteresis on the
// re-expand prevents flicker (see tab-collapse.ts). Also lifts the state
// to the global `data-compact` attribute on <html> so any surface can
// react to the same trigger via shared/responsive/compact.ts.
//
// Returns the reactive collapsed state plus ref setters for the tabs
// wrapper + nav elements the measurement reads.
export interface ResponsiveTabs {
  tabsIconOnly: Accessor<boolean>;
  brandSealOnly: Accessor<boolean>;
  setTabsWrap: (el: HTMLDivElement) => void;
  setTabsNav: (el: HTMLElement) => void;
}

// Re-expand threshold — must exceed the layout swing the brand collapse
// produces (≈50px gained by the tabs cell when the brand drops to
// seal-only), so widening past the collapse point doesn't oscillate.
const HYSTERESIS_PX = 64;

export function useResponsiveTabs(): ResponsiveTabs {
  const [tabsIconOnly, setTabsIconOnly] = createSignal(false);
  // brandSealOnly shares the signal surface so the data attributes react
  // together.
  const brandSealOnly = tabsIconOnly;
  let collapseAtVw = 0;
  let tabsWrapEl: HTMLDivElement | undefined;
  let tabsNavEl: HTMLElement | undefined;

  onMount(() => {
    if (!tabsWrapEl || !tabsNavEl) return;
    const tabsWrap = tabsWrapEl;
    const tabsNav = tabsNavEl;
    const vwNow = () =>
      typeof document !== "undefined" ? document.documentElement.clientWidth : 0;
    const update = () => {
      const next = nextTabCollapseState({
        iconOnly: tabsIconOnly(),
        vw: vwNow(),
        collapseAtVw,
        wrapWidth: tabsWrap.clientWidth,
        navScrollWidth: tabsNav.scrollWidth,
        hysteresisPx: HYSTERESIS_PX,
      });
      collapseAtVw = next.collapseAtVw;
      if (next.iconOnly !== tabsIconOnly()) setTabsIconOnly(next.iconOnly);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(tabsWrap);
    ro.observe(document.body);
    // Re-measure once fonts settle so the natural width reflects the real
    // text width rather than the fallback-font estimate.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(update);
    }
    onCleanup(() => ro.disconnect());
  });

  // Lift to a global data-compact attribute on <html> so any responsive
  // surface can react to the same measured signal (single trigger instead
  // of a sprawl of independent media queries).
  createEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.compact = tabsIconOnly() ? "true" : "false";
  });
  onCleanup(() => {
    if (typeof document !== "undefined") delete document.documentElement.dataset.compact;
  });

  return {
    tabsIconOnly,
    brandSealOnly,
    setTabsWrap: (el) => (tabsWrapEl = el),
    setTabsNav: (el) => (tabsNavEl = el),
  };
}
